import md5 from 'md5';
import { capitalize } from 'vue';
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import multiguard from 'vue-router-multiguard';

import UserRoles from '@/common/enums/user-roles.enum';
import OrganizationType from '@/common/models/memberships/OrganizationType.enum';
import Permission from '@/common/models/memberships/Permission';
import EditExchangeDeal from '@/exch-app/pages/EditExchangeDeal.vue';
import ExchangeDealDetails from '@/exch-app/pages/ExchangeDealDetails.vue';
import ExchangeDealList from '@/exch-app/pages/ExchangeDealList.vue';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { region, Regions } from '@/globals';
import SessionCache from '@/SessionCache';
import rootStore from '@/store';
import * as rootTypes from '@/store/mutation-types';
import rootState from '@/store/state';
import LocalCache from '@/utils/LocalCache';
import i18n from '@/vueI18n';

const Login = () => import('@/common/components/Login.vue');
const Organization = () => import('@/common/components/Organization.vue');
const PasswordResetRequest = () => import('@/common/components/PasswordResetRequest.vue');
const ResetPassword = () => import('@/common/components/ResetPassword.vue');
const VerifyEmail = () => import('@/common/components/VerifyEmail.vue');
const AudienceAuditDetails = () => import('@/org-app/components/audienceAudit/AudienceAuditDetails.vue');
const AudienceLogicDetails = () => import('@/org-app/components/audienceLogics/AudienceLogicDetails.vue');
const AudienceLogicsList = () => import('@/org-app/components/audienceLogics/AudienceLogicsList.vue');

const EditAirportAudience = () => import('@/org-app/components/audienceLogics/EditAirportAudience.vue');
const EditAudienceLogic = () => import('@/org-app/components/audienceLogics/EditAudienceLogic.vue');
const EditMovieAudience = () => import('@/org-app/components/audienceLogics/EditMovieAudience.vue');
const AvailsImportFilesList = () => import('@/org-app/components/avails/AvailsImportFilesList.vue');

const CampaignsList = () => import('@/org-app/components/campaigns/CampaignsList.vue');
const CampaignDetails = () => import('@/org-app/components/campaigns/CampaignDetails.vue');
const EditCampaign = () => import('@/org-app/components/campaigns/EditCampaign.vue');

const CreativesList = () => import('@/org-app/components/creatives/CreativesList.vue');
const CreativeDetails = () => import('@/org-app/components/creatives/CreativeDetails.vue');
const EditCreative = () => import('@/org-app/components/creatives/EditCreative.vue');
const ExternalCreativeDetails = () => import('@/org-app/components/creatives/ExternalCreativeDetails.vue');
const EditExternalCreative = () => import('@/org-app/components/creatives/EditExternalCreative.vue');

const AudienceFilesList = () => import('@/org-app/components/audienceFiles/AudienceFilesList.vue');
const AudienceFileDetails = () => import('@/org-app/components/audienceFiles/AudienceFileDetails.vue');
const EditAudienceFile = () => import('@/org-app/components/audienceFiles/EditAudienceFile.vue');

const PostalCodesFilesList = () => import('@/org-app/components/postalCodesFiles/PostalCodesFilesList.vue');
const EditPostalCodesFile = () => import('@/org-app/components/postalCodesFiles/EditPostalCodesFile.vue');

const AudienceGeofenceGroupsList = () => import('@/org-app/components/geofenceGroups/AudienceGeofenceGroupsList.vue');
const GeofenceGroupDetails = () => import('@/org-app/components/geofenceGroups/GeofenceGroupDetails.vue');
const EditAudienceGeofenceGroup = () => import('@/org-app/components/geofenceGroups/EditAudienceGeofenceGroup.vue');
const EditProximityGeofenceGroup = () => import('@/org-app/components/geofenceGroups/EditProximityGeofenceGroup.vue');
const ProximityGeofenceGroupsList = () => import('@/org-app/components/geofenceGroups/ProximityGeofenceGroupsList.vue');

const LineItemsList = () => import('@/org-app/components/lineItems/LineItemsList.vue');
const LineItemDetails = () => import('@/org-app/components/lineItems/LineItemDetails.vue');
const EditLineItem = () => import('@/org-app/components/lineItems/EditLineItemV2.vue');

const OrgDealsList = () => import('@/org-app/components/packages/OrgDealsList.vue');
const OrgDealDetails = () => import('@/org-app/components/packages/OrgDealDetails.vue');
const EditOrgDeal = () => import('@/org-app/components/packages/EditOrgDeal.vue');
const CuratorDealsList = () => import('@/org-app/components/curatorDeals/CuratorDealsList.vue');
const CuratorSupplierDealsList = () => import('@/org-app/components/curatorDeals/CuratorSupplierDealsList.vue');
const CuratorDealDetails = () => import('@/org-app/components/packages/OrgDealDetails.vue');
const EditCuratorDeal = () => import('@/org-app/components/packages/EditOrgDeal.vue');

const PreSaleHeatmapDetails = () => import('@/org-app/components/preSaleHeatmaps/PreSaleHeatmapDetails.vue');
const EditPreSaleHeatmap = () => import('@/org-app/components/preSaleHeatmaps/EditPreSaleHeatmap.vue');
const PreSaleHeatmapViewer = () => import('@/org-app/components/preSaleHeatmaps/PreSaleHeatmapViewer.vue');

const LiftDashboardDetails = () => import('@/org-app/components/liftDashboards/LiftDashboardDetails.vue');
const EditLiftDashboard = () => import('@/org-app/components/liftDashboards/EditLiftDashboard.vue');
const LiftDashboardViewer = () => import('@/org-app/components/liftDashboards/LiftDashboardViewer.vue');

const ReportDefinitionsList = () => import('@/org-app/components/reportDefinitions/ReportDefinitionsList.vue');
const ReportDefinitionDetails = () => import('@/org-app/components/reportDefinitions/ReportDefinitionDetails.vue');
const EditReportDefinition = () => import('@/org-app/components/reportDefinitions/EditReportDefinition.vue');
const AdvancedReportsList = () => import('@/org-app/components/reportDefinitions/AdvancedReportsList.vue');
const AdvancedReportDefinition = () => import('@/org-app/components/reportDefinitions/AdvancedReportDefinition.vue');

const Dashboard = () => import('@/org-app/components/dashboards/Dashboard.vue');
const PendingSubmissions = () => import('@/org-app/components/dashboards/PendingSubmissions.vue');
const PendingApprovals = () => import('@/org-app/components/pending-approvals/PendingApprovals.vue');
const RevenueSplit = () => import('@/org-app/components/dashboards/RevenueSplit.vue');

const NetworksList = () => import('@/org-app/components/networks/NetworksList.vue');
const NetworkDetails = () => import('@/org-app/components/networks/NetworkDetails.vue');
const EditNetwork = () => import('@/org-app/components/networks/EditNetwork.vue');

const SitesList = () => import('@/org-app/components/sites/SitesList.vue');
const SiteDetails = () => import('@/org-app/components/sites/SiteDetails.vue');
const EditSite = () => import('@/org-app/components/sites/EditSite.vue');

const UnitsList = () => import('@/org-app/components/units/UnitsList.vue');
const UnitDetails = () => import('@/org-app/components/units/UnitDetails.vue');
const EditUnit = () => import('@/org-app/components/units/EditUnit.vue');

const UnitPacksList = () => import('@/org-app/components/unitPacks/UnitPacksList.vue');
const UnitPackDetails = () => import('@/org-app/components/unitPacks/UnitPackDetails.vue');
const EditUnitPack = () => import('@/org-app/components/unitPacks/EditUnitPack.vue');

const JourneyList = () => import('@/org-app/components/journey/JourneyList.vue');
const EditJourney = () => import('@/org-app/components/journey/EditJourney.vue');
const ViewJourney = () => import('@/org-app/components/journey/ViewJourney.vue');
const CrunchJourney = () => import('@/org-app/components/journey/CrunchJourney.vue');

const OldJourneyAnalysisList = () => import('@/org-app/components/journeyAnalysis/JourneyAnalysisList.vue');
const OldJourneyAnalysisDetails = () => import('@/org-app/components/journeyAnalysis/JourneyAnalysisDetails.vue');
const EditOldJourneyAnalysis = () => import('@/org-app/components/journeyAnalysis/EditJourneyAnalysis.vue');

const OrganizationSettingsDetails = () =>
  import('@/org-app/components/organizationSettings/OrganizationSettingsDetails.vue');

const OrganizationCustomFieldsList = () =>
  import('@/org-app/components/organizationCustomFields/OrganizationCustomFieldsList.vue');
const OrganizationCustomFieldDetails = () =>
  import('@/org-app/components/organizationCustomFields/OrganizationCustomFieldDetails.vue');
const EditOrganizationCustomField = () =>
  import('@/org-app/components/organizationCustomFields/EditOrganizationCustomField.vue');

const CustomEventsList = () => import('@/org-app/components/customEvents/CustomEventsList.vue');
const CustomEventDetails = () => import('@/org-app/components/customEvents/CustomEventDetails.vue');
const EditCustomEvent = () => import('@/org-app/components/customEvents/EditCustomEvent.vue');
const CustomEventsCategoriesList = () => import('@/org-app/components/customEvents/CustomEventsCategoriesList.vue');

const EditHeaderBidderPreset = () => import('@/org-app/components/headerBidder/EditHeaderBidderPreset.vue');
const HeaderBidderDashboard = () => import('@/org-app/components/headerBidder/HeaderBidderDashboard.vue');
const EditMobileLineItem = () => import('@/org-app/components/mobileLineItems/EditMobileLineItem.vue');
const MobileLineItemDetails = () => import('@/org-app/components/mobileLineItems/MobileLineItemDetails.vue');

const YieldManagerDashboard = () => import('@/org-app/components/yieldManager/YieldManagerDashboard.vue');
const HeaderBidderSettingsGateway = () =>
  import('@/org-app/components/headerBidder/settings/HeaderBidderSettingsGateway.vue');

const EditOperatingHours = () => import('@/org-app/components/inventory/EditOperatingHours.vue');
const Forbidden = () => import('@/common/components/Forbidden.vue');
const ServerDown = () => import('@/common/components/ServerDown.vue');

const UsersList = () => import('@/org-app/components/users/UsersList.vue');

const PlanOffer = () => import('@/org-app/components/plans/PlanOffer.vue');
const PlansList = () => import('@/org-app/components/plans/PlansList.vue');
const PlanDetailsWrapper = () => import('@/org-app/components/plans/PlanDetailsWrapper.vue');
const SharedCampaignDetailsViewer = () =>
  import('@/org-app/components/campaigns/sharedCampaign/SharedCampaignDetailsViewer.vue');
const SharedPlanDetailsViewer = () => import('@/org-app/components/plans/sharedPlan/SharedPlanDetailsViewer.vue');
const EditPlan = () => import('@/org-app/components/plans/EditPlan.vue');
const SharedPlanEditor = () => import('@/org-app/components/plans/sharedPlan/SharedPlanEditor.vue');

import GoogleAnalyticsApi from '@/common/apis/GoogleAnalyticsApi';
import APIProxy from '@/org-app/api';
import isLiveboard from '@/utils/IsLiveboard';

import { waitUntil } from './utils/DelayHelper';

const loginPath: RouteRecordRaw = {
  path: '/login',
  component: Login,
  meta: {
    title: 'actions.login',
    public: true,
  },
};

const membershipSelection = {
  path: '/organization-selection',
  component: Organization,
  meta: { title: 'names.organization' },
};

const forbidden = {
  path: '/forbidden',
  component: Forbidden,
  meta: { title: 'messages.accessDenied' },
  props: {
    header: () => i18n.global.t('messages.accessDenied'),
    details: () => i18n.global.t('messages.accessDeniedDescription'),
  },
};

const serverDown = {
  path: '/oops',
  component: ServerDown,
  meta: {
    title: 'messages.serverDown',
    public: true,
  },
};

const orgAccessDenied = {
  path: '/orgAccessDenied',
  component: Forbidden,
  meta: { title: 'messages.accessDenied' },
  props: {
    header: () => i18n.global.t('messages.accessDenied'),
    details: () => i18n.global.t('messages.pageAccessDenied'),
  },
};

const paidFeatureAccessDenied = {
  path: '/paidFeatureAccessDenied',
  component: Forbidden,
  meta: { title: 'messages.accessDenied' },
  props: {
    header: () => i18n.global.t('messages.accessDenied'),
    details: () => i18n.global.t('messages.contactHivestackSales'),
  },
};

const isSupplyOrganizationRoute = (to, from, next) => {
  const organization = LocalCache.get('organization');
  const isSupplyOrganization = organization.type === 'supply';
  if (isSupplyOrganization) {
    return next();
  }
  return next('/');
};

const handleOrgSettingAccess =
  (settingName, redirectPath = '/') =>
  (to, from, next) => {
    const { organizationSettings } = rootState.orgApp;
    if (organizationSettings !== null && organizationSettings !== undefined && organizationSettings[settingName]) {
      next();
    } else {
      next(redirectPath);
    }
  };

// paid features are shown into the app, where the user has the org setting enabled or not
// but if org setting is set to false, the user will be redirect to the forbidden page
const handlePaidFeatureAccess =
  (settingName, redirectPath = paidFeatureAccessDenied.path) =>
  (to, from, next) => {
    const { organizationSettings } = rootState.orgApp;
    if (
      isLiveboard() ||
      (organizationSettings !== null && organizationSettings !== undefined && organizationSettings[settingName])
    ) {
      next();
    } else {
      next(redirectPath);
    }
  };

const handleDemandOrgFeatureAccess = (features) => (to, from, next) => {
  const { organization } = rootState.orgApp;
  if (organization.isSupplyOrganization) {
    return next();
  }
  const featureNames = Array.isArray(features) ? features : [features];
  if (
    organization.organizationFeatures &&
    featureNames.map((x) => organization.organizationFeatures[x]).some((x) => x)
  ) {
    return next();
  }
  return next('/');
};

const handleSupplyOrgFeatureAccess = (features) => (to, from, next) => {
  const { organization, organizationSettings } = rootState.orgApp;
  if (organization?.isDemandOrganization) {
    return next();
  }
  const featureNames = Array.isArray(features) ? features : [features];
  if (
    organization?.organizationFeatures &&
    featureNames.map((x) => organization?.organizationFeatures[x]).some((x) => x)
  ) {
    return next();
  }
  if (featureNames.includes('adServer') && organizationSettings?.uiEnableLiAccessIfAdServerOff) {
    return next();
  }
  return next('/');
};

const handleOrgFeatureAccess = (features) => (to, from, next) => {
  const { organization } = rootState.orgApp;
  const featureNames = Array.isArray(features) ? features : [features];
  if (
    organization &&
    (organization?.type === OrganizationType.CURATOR || // Curator orgs don't have features, bypassing feature check for now.
      (organization?.organizationFeatures && featureNames.map((x) => organization?.organizationFeatures[x])).some(
        (x) => x,
      ))
  ) {
    return next();
  }
  return next('/');
};

const handlePermissions = (action, entity) => async (to, from, next) => {
  let permissions = null;
  if (rootState.chosenMembershipType === 'org') {
    await waitUntil(() => rootState.orgApp.permissions != null, 500, 5000);
    permissions = rootState.orgApp.permissions;
  } else {
    await waitUntil(() => rootState.exchApp.permissions != null, 500, 5000);
    permissions = rootState.exchApp.permissions;
  }

  if (permissions?.hasPermission(action, entity)) {
    next();
    return;
  }
  GlobalEventBus.$emit(GlobalEvents.error, i18n.global.t('messages.permissionDenied'));
  next(forbidden);
};

const isOrganizationRoute = (to, from, next) => {
  const chosenMembershipType = LocalCache.get('chosenMembershipType');
  const userRole = LocalCache.get('userRole');

  // Sidekick
  const isSidekickOrganization = rootStore.getters['orgApp/isSidekickOrganization'];
  if (isSidekickOrganization && !canAccessSidekickOrganizationRoute(to, from)) {
    return next(forbidden);
  }

  // Sales planner | Planner reserve campaign
  if (userRole?.code === UserRoles.SALES_PLANNER || userRole?.code === UserRoles.PLANNER_RESERVE_CAMPAIGN_ID) {
    const toPath = `/${to?.path?.split('/')[1]}`;
    const fromPath = `/${from?.path?.split('/')[1]}`;
    let pathsPermitted = ['/plans', '/shared-plan'];
    const specificFromPathsPermitted = ['/organization-selection'];

    if (userRole?.code === UserRoles.PLANNER_RESERVE_CAMPAIGN_ID) {
      pathsPermitted = [...pathsPermitted, '/proximityGeofenceGroups', '/campaigns'];
    }

    if (
      !pathsPermitted.includes(toPath) &&
      !pathsPermitted.includes(fromPath) &&
      !specificFromPathsPermitted.includes(fromPath)
    ) {
      GlobalEventBus.$emit('error', i18n.global.t('messages.permissionDenied'));
      return next(forbidden);
    }
  }

  // Default
  rootStore.dispatch('setChosenMembershipType', chosenMembershipType);
  const { hasChosenOrganization } = rootStore.getters;
  if (hasChosenOrganization) {
    return next();
  }
  rootStore.dispatch('clearMembershipType');
  return next(membershipSelection);
};

const canAccessSidekickOrganizationRoute = (to, from) => {
  const toPath = `/${to?.path?.split('/')[1]}`;
  const fromPath = `/${from?.path?.split('/')[1]}`;
  const pathsNotPermitted = ['/lineitems', '/pending-submissions', '/campaigns', '/custom-events'];

  if (pathsNotPermitted.includes(toPath) || pathsNotPermitted.includes(fromPath)) {
    GlobalEventBus.$emit('error', i18n.global.t('messages.permissionDenied'));
    return false;
  }
  return true;
};

const isExchangeRoute = (to, from, next) => {
  const chosenMembershipType = LocalCache.get('chosenMembershipType');
  rootStore.dispatch('setChosenMembershipType', chosenMembershipType);
  const { hasChosenExchange } = rootStore.getters;
  if (hasChosenExchange) {
    return next();
  }
  rootStore.dispatch('clearMembershipType');
  return next(membershipSelection);
};

const isNotChina = (to, from, next) => {
  const isChina = region === Regions.China;
  if (!isChina) {
    return next();
  }
  rootStore.dispatch('clearMembershipType');
  return next(membershipSelection);
};

const hasPurchaseType = (to, from, next) => {
  if (to.params.planID != null && to.params.packagePurchaseType == null) {
    // Redirect to plan details page
    return next({
      path: `plans/${to.params.planID}`,
      replace: true,
    });
  }
  return next();
};

const routes: RouteRecordRaw[] = [
  loginPath,
  {
    // The Request
    path: '/password-reset',
    component: PasswordResetRequest,
    meta: {
      title: 'actions.sendEmail',
      public: true,
    },
  },
  {
    // The act of resetting one's password
    path: '/reset-password',
    component: ResetPassword,
    meta: {
      title: 'actions.resetPassword',
      public: true,
    },
  },
  {
    // The act of verifying one's email
    path: '/verify-email',
    component: VerifyEmail,
    meta: {
      title: 'actions.verifyEmail',
      public: true,
    },
  },
  membershipSelection,
  forbidden,
  orgAccessDenied,
  serverDown,
  paidFeatureAccessDenied,

  //
  // Public routes
  //
  {
    path: '/',
    component: Dashboard,
  },
  {
    path: '/heat/:id',
    component: PreSaleHeatmapViewer,
    meta: {
      title: 'names.heatmap',
      public: true,
    },
  },
  {
    path: '/lift/:id',
    component: LiftDashboardViewer,
    meta: {
      title: 'names.liftDashboard',
      public: true,
    },
  },
  {
    path: '/proposal/:id/',
    component: SharedPlanDetailsViewer,
    meta: { title: 'names.plan', public: true },
  },
  {
    path: '/monitoring/:uuid/',
    component: SharedCampaignDetailsViewer,
    meta: { title: 'names.campaign', public: true },
  },

  // Demand-side
  {
    path: '/pending-submissions',
    component: PendingSubmissions,
    meta: { title: 'names.dashboard', titlePlural: 2 },
    beforeEnter: multiguard([isOrganizationRoute, handleOrgFeatureAccess('approvals')]),
  },
  {
    path: '/campaigns',
    component: CampaignsList,
    meta: { title: 'names.campaign', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/campaigns/:id',
    component: CampaignDetails,
    meta: { title: 'names.campaign' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/campaigns/:id/edit',
    component: EditCampaign,
    meta: { title: 'actions.editCampaign' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.CAMPAIGN),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/campaigns/:campaignID/pre-sale-heatmaps/:id/edit',
    component: EditPreSaleHeatmap,
    meta: { title: 'actions.editPresaleHeatmap' },
    beforeEnter: multiguard([
      isNotChina,
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.PRESALEHEATMAP),
      handleOrgSettingAccess('uiEnablePreSaleHeatmaps'),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/custom-events',
    component: CustomEventsCategoriesList,
    meta: { title: 'names.customEvent', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.READ, Permission.entities.CUSTOMEVENT),
      handleOrgFeatureAccess(['adServer', 'dspCampaigns', 'dspDeals', 'ssp']),
      handleOrgSettingAccess('uiEnableCustomEventManagement'),
    ]),
  },
  {
    path: '/custom-events/:id/edit/:category_name?',
    component: EditCustomEvent,
    meta: { title: 'actions.editCustomEvent' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.CUSTOMEVENT),
      handleOrgFeatureAccess(['adServer', 'dspCampaigns', 'dspDeals', 'ssp']),
      handleOrgSettingAccess('uiEnableCustomEventManagement'),
    ]),
  },
  {
    path: '/custom-events/:category_name',
    component: CustomEventsList,
    meta: { title: 'names.customEvent', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.READ, Permission.entities.CUSTOMEVENT),
      handleOrgFeatureAccess(['adServer', 'dspCampaigns', 'dspDeals', 'ssp']),
      handleOrgSettingAccess('uiEnableCustomEventManagement'),
    ]),
  },
  {
    path: '/custom-events/:category_name/:id',
    component: CustomEventDetails,
    meta: { title: 'names.customEvent' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.READ, Permission.entities.CUSTOMEVENT),
      handleOrgFeatureAccess(['adServer', 'dspCampaigns', 'dspDeals', 'ssp']),
      handleOrgSettingAccess('uiEnableCustomEventManagement'),
    ]),
  },
  {
    path: '/pre-sale-heatmaps/:id',
    component: PreSaleHeatmapDetails,
    meta: {
      title: 'names.preSaleHeatmap',
      id: 'presale-heatmap-details',
    },
    beforeEnter: multiguard([
      isNotChina,
      isOrganizationRoute,
      handleOrgSettingAccess('uiEnablePreSaleHeatmaps'),
      handleOrgFeatureAccess(['adServer', 'dspCampaigns']),
    ]),
  },
  {
    path: '/pre-sale-heatmaps/:id/edit',
    component: EditPreSaleHeatmap,
    meta: { title: 'actions.editPresaleHeatmap' },
    beforeEnter: multiguard([
      isNotChina,
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.PRESALEHEATMAP),
      handleOrgSettingAccess('uiEnablePreSaleHeatmaps'),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/campaigns/:campaignID/lift-dashboards/:id/edit',
    component: EditLiftDashboard,
    meta: { title: 'actions.editLiftDashboard' },
  },
  {
    path: '/campaigns/:campaignID/lift-dashboards/:id',
    component: LiftDashboardDetails,
    meta: {
      title: 'names.liftDashboard',
      id: 'lift-dashboard-details',
    },
  },
  {
    path: '/lift-dashboards/:id',
    component: LiftDashboardDetails,
    meta: {
      title: 'names.liftDashboard',
      id: 'lift-dashboard-details',
    },
  },
  {
    path: '/lift-dashboards/:id/edit',
    component: EditLiftDashboard,
    meta: { title: 'actions.editLiftDashboard' },
  },
  {
    path: '/creatives',
    component: CreativesList,
    meta: { title: 'names.creative', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleSupplyOrgFeatureAccess(['adServer', 'creatives']),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/creatives/:id',
    component: CreativeDetails,
    meta: { title: 'names.creative' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleSupplyOrgFeatureAccess(['adServer', 'creatives']),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/creatives/:id/edit',
    component: EditCreative,
    meta: { title: 'actions.editCreative' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.CREATIVE),
      handleSupplyOrgFeatureAccess(['adServer', 'creatives']),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/externalcreatives',
    component: CreativesList,
    props: { isExternalCreatives: true },
    meta: { title: 'names.externalCreative', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleSupplyOrgFeatureAccess(['adServer', 'creatives']),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/externalcreatives/:id',
    component: ExternalCreativeDetails,
    meta: { title: 'names.creative' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleSupplyOrgFeatureAccess(['adServer', 'creatives']),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/externalcreatives/:id/edit',
    component: EditExternalCreative,
    meta: { title: 'actions.editCreative' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.CREATIVE),
      handleSupplyOrgFeatureAccess(['adServer', 'creatives']),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    name: 'new-lineitem-new-campaign',
    path: '/campaigns/:campaignID/lineitems/:id/edit',
    component: EditLineItem,
    meta: { title: 'actions.editLineItem' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.LINEITEM),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/reportdefinitions',
    component: ReportDefinitionsList,
    meta: { title: 'names.reportDefinition', titlePlural: 2 },
    beforeEnter: multiguard([isOrganizationRoute, handleOrgFeatureAccess('reporting')]),
  },
  {
    path: '/reportdefinitions/:id',
    component: ReportDefinitionDetails,
    meta: { title: 'names.reportDefinition' },
    beforeEnter: multiguard([isOrganizationRoute, handleOrgFeatureAccess('reporting')]),
  },
  {
    path: '/reportdefinitions/:id/edit',
    component: EditReportDefinition,
    meta: { title: 'actions.editReportDefinition' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.REPORTDEFINITION),
      handleOrgFeatureAccess('reporting'),
    ]),
  },
  {
    path: '/plans/:id/edit',
    component: EditPlan,
    meta: { title: 'actions.editPlan' },
    props: { isNew: false },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.READ, Permission.entities.PLAN),
      handleOrgSettingAccess('enablePlansFeature'),
    ]),
  },
  {
    path: '/plans/:id/edit/duplicate/:fromPlanID',
    component: EditPlan,
    meta: { title: 'actions.editPlan' },
    props: { isNew: false },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.READ, Permission.entities.PLAN),
      handleOrgSettingAccess('enablePlansFeature'),
    ]),
  },
  {
    path: '/plans',
    component: PlansList,
    meta: { title: 'names.plan', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.READ, Permission.entities.PLAN),
      isLiveboard()
        ? handleOrgSettingAccess('enablePlansFeature', '/plan-offer')
        : handlePaidFeatureAccess('enablePlansFeature', '/plan-offer'),
    ]),
  },
  {
    path: '/plan-offer',
    component: PlanOffer,
    meta: { title: 'names.plan', titlePlural: 2 },
    beforeEnter: multiguard([isOrganizationRoute]),
  },
  {
    path: '/plans/:id/',
    component: PlanDetailsWrapper,
    meta: { title: 'names.plan' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.READ, Permission.entities.PLAN),
      handleOrgSettingAccess('enablePlansFeature'),
    ]),
  },
  {
    path: '/shared-plan/:id/edit',
    component: SharedPlanEditor,
    meta: { title: 'actions.editSharedPlan' },
    props: { isNew: false },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.READ, Permission.entities.PLAN),
      handleOrgSettingAccess('enablePlansFeature'),
    ]),
  },
  {
    path: '/proposal/:id/',
    component: SharedPlanDetailsViewer,
    meta: { title: 'names.plan', public: true },
  },
  {
    path: '/monitoring/:uuid/',
    component: SharedCampaignDetailsViewer,
    meta: { title: 'names.campaign', public: true },
  },

  // Curator
  {
    path: '/curated-deals',
    component: CuratorDealsList,
    meta: { title: 'names.curatedDeal', titlePlural: 2 },
    beforeEnter: multiguard([isOrganizationRoute]),
  },
  {
    path: '/curator-supplier-deals',
    component: CuratorSupplierDealsList,
    meta: { title: 'names.marketplace' },
    beforeEnter: multiguard([isOrganizationRoute]),
  },
  {
    name: 'curator-package-details',
    path: '/curate/deals/:id',
    component: CuratorDealDetails,
    meta: { title: 'names.deal' },
    beforeEnter: multiguard([isOrganizationRoute]),
  },
  {
    path: '/curate/deals/:id/edit',
    component: EditCuratorDeal,
    meta: { title: 'actions.editDeal' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.PACKAGE),
    ]),
    props: (route) => ({ key: route.params.id }),
  },
  {
    path: '/curate/:id/edit/duplicate/:fromCuratePackageID',
    component: EditCuratorDeal,
    meta: { title: 'actions.editDeal' },
    props: true,
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.PACKAGE),
    ]),
  },

  // Supply-side
  {
    path: '/deals',
    component: OrgDealsList,
    meta: { title: 'names.deal', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleSupplyOrgFeatureAccess('ssp'),
      handleDemandOrgFeatureAccess(['dspDeals']),
    ]),
  },
  {
    name: 'package-details',
    path: '/deals/:id',
    component: OrgDealDetails,
    meta: { title: 'names.deal' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleSupplyOrgFeatureAccess('ssp'),
      handleDemandOrgFeatureAccess(['dspDeals']),
    ]),
  },
  {
    path: '/deals/:id/edit',
    component: EditOrgDeal,
    meta: { title: 'actions.editDeal' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.PACKAGE),
      handleSupplyOrgFeatureAccess('ssp'),
      handleDemandOrgFeatureAccess(['dspDeals']),
    ]),
    props: (route) => ({ key: route.params.id }),
  },
  {
    name: 'convert-plan-package-creation',
    path: '/deals/:id/edit/plan/:planID/purchaseType/:packagePurchaseType',
    component: EditOrgDeal,
    props: true,
    meta: { title: 'actions.editLineItem' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.PACKAGE),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspDeals']),
      hasPurchaseType,
    ]),
  },
  {
    path: '/deals/:id/edit/duplicate/:fromPackageID',
    component: EditOrgDeal,
    meta: { title: 'actions.editLineItem' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.PACKAGE),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspDeals']),
    ]),
  },
  {
    path: '/lineitems',
    component: LineItemsList,
    props: true,
    meta: { title: 'names.lineItem', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleSupplyOrgFeatureAccess(['adServer', 'ssp']),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/creatives/state/:state',
    component: CreativesList,
    props: true,
    meta: { title: 'names.creative', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleSupplyOrgFeatureAccess(['adServer', 'creatives']),
      handleDemandOrgFeatureAccess(['dspCampaigns', 'dspDeals']),
    ]),
  },
  {
    path: '/pendingapprovals',
    component: PendingApprovals,
    props: true,
    meta: { title: 'names.pendingApproval', titlePlural: 2 },
    beforeEnter: multiguard([isOrganizationRoute, handleOrgFeatureAccess('approvals')]),
  },
  {
    path: '/pendingapprovals/lineitems/:id',
    component: LineItemDetails,
    props: true,
    meta: { title: 'names.lineItem' },
    beforeEnter: multiguard([isOrganizationRoute, handleOrgFeatureAccess('approvals')]),
  },
  {
    path: '/pendingapprovals/creatives/:id',
    component: CreativeDetails,
    props: true,
    meta: { title: 'names.creative' },
    beforeEnter: multiguard([isOrganizationRoute, handleOrgFeatureAccess('approvals')]),
  },
  {
    path: '/pendingapprovals/externalcreatives/:id',
    component: ExternalCreativeDetails,
    props: true,
    meta: { title: 'names.externalCreative' },
    beforeEnter: multiguard([isOrganizationRoute, handleOrgFeatureAccess('approvals')]),
  },
  {
    path: '/revenuesplit',
    component: RevenueSplit,
    props: true,
    meta: { title: 'names.revenueSplit' },
    beforeEnter: multiguard([isOrganizationRoute, handleOrgFeatureAccess('dashboard')]),
  },
  //
  // All
  //
  {
    path: '/lineitems/:id',
    component: LineItemDetails,
    meta: { title: 'names.lineItem' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/lineitems/:id/edit',
    component: EditLineItem,
    meta: { title: 'actions.editLineItem' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.LINEITEM),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/lineitems/:id/edit/duplicate/:fromLineItemID',
    component: EditLineItem,
    meta: { title: 'actions.editLineItem' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.LINEITEM),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/lineitems/:id/edit/plan/:planID',
    component: EditLineItem,
    meta: { title: 'actions.editLineItem' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.LINEITEM),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/lineitems/:id/edit/package/:packageID',
    component: EditLineItem,
    meta: { title: 'actions.editLineItem' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.LINEITEM),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/lineitems/:id/edit/curated-package/:curatedPackageID',
    component: EditLineItem,
    meta: { title: 'actions.editLineItem' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.LINEITEM),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/mobilelineitems/:id(\\d+)',
    component: MobileLineItemDetails,
    meta: { title: 'names.mobileLineItem' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/mobilelineitems/new',
    component: EditMobileLineItem,
    meta: { title: 'actions.newMobileLineItem' },
    props: { isNew: true },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.MOBILELINEITEM),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/mobilelineitems/:id(\\d+)/edit',
    component: EditMobileLineItem,
    meta: { title: 'actions.editMobileLineItem' },
    props: { isNew: false },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.MOBILELINEITEM),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/campaigns/:campaign_id(\\d+)/mobilelineitems/new',
    component: EditMobileLineItem,
    meta: { title: 'actions.editMobileLineItem' },
    props: {
      isNew: true,
      fromCampaign: true,
    },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.MOBILELINEITEM),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns']),
    ]),
  },
  {
    path: '/dashboard',
    component: Dashboard,
    alias: [
      '/mediaownerdashboard',
      '/mediaownerdashboard-beta',
      '/buyerdashboard',
      '/buyerdashboard-beta',
      '/curatordashboard',
    ],
    meta: { title: 'names.dashboard' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleOrgFeatureAccess('dashboard'),
      handlePermissions(Permission.actions.READ, Permission.entities.ORGANIZATIONPERFORMANCE),
    ]),
  },
  {
    path: '/geofenceGroups/:id',
    component: GeofenceGroupDetails,
    meta: { title: 'names.geofenceGroup' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleOrgFeatureAccess('customAudiences'),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns', 'dspDeals']),
      handleOrgSettingAccess('uiEnableGeofenceGroups'),
    ]),
  },
  {
    path: '/audienceGeofenceGroups/:id',
    component: GeofenceGroupDetails,
    meta: { title: 'names.geofenceGroup' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleOrgFeatureAccess('customAudiences'),
      handleOrgSettingAccess('uiEnableAudienceLogics'),
      handleOrgSettingAccess('uiEnableGeofenceGroups'),
    ]),
  },
  {
    path: '/audienceGeofenceGroups/:id/edit',
    component: EditAudienceGeofenceGroup,
    meta: { title: 'actions.editAudienceGeofenceGroup' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleOrgFeatureAccess('customAudiences'),
      handleOrgSettingAccess('uiEnableAudienceLogics'),
      handleOrgSettingAccess('uiEnableGeofenceGroups'),
      handlePermissions(Permission.actions.WRITE, Permission.entities.GEOFENCEGROUP),
    ]),
  },
  {
    path: '/audienceGeofenceGroups/:id/edit/duplicate/:fromGroupID',
    component: EditAudienceGeofenceGroup,
    meta: { title: 'actions.editAudienceGeofenceGroup' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleOrgFeatureAccess('customAudiences'),
      handleOrgSettingAccess('uiEnableAudienceLogics'),
      handleOrgSettingAccess('uiEnableGeofenceGroups'),
      handlePermissions(Permission.actions.WRITE, Permission.entities.GEOFENCEGROUP),
    ]),
  },
  {
    path: '/proximityGeofenceGroups/:id',
    component: GeofenceGroupDetails,
    meta: { title: 'names.geofenceGroup' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleSupplyOrgFeatureAccess(['adServer', 'inventoryManagement']),
      handleDemandOrgFeatureAccess(['dspCampaigns', 'dspDeals']),
      handleOrgSettingAccess('uiEnableGeofenceGroups'),
    ]),
  },
  {
    path: '/proximityGeofenceGroups/:id/edit',
    component: EditProximityGeofenceGroup,
    meta: { title: 'actions.editProximityGeofenceGroup' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleSupplyOrgFeatureAccess(['adServer', 'inventoryManagement']),
      handleDemandOrgFeatureAccess(['dspCampaigns', 'dspDeals']),
      handleOrgSettingAccess('uiEnableGeofenceGroups'),
      handlePermissions(Permission.actions.WRITE, Permission.entities.GEOFENCEGROUP),
    ]),
  },
  {
    path: '/proximityGeofenceGroups/:id/edit/duplicate/:fromGroupID',
    component: EditProximityGeofenceGroup,
    meta: { title: 'actions.editProximityGeofenceGroup' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleSupplyOrgFeatureAccess(['adServer', 'inventoryManagement']),
      handleDemandOrgFeatureAccess(['dspCampaigns', 'dspDeals']),
      handleOrgSettingAccess('uiEnableGeofenceGroups'),
      handlePermissions(Permission.actions.WRITE, Permission.entities.GEOFENCEGROUP),
    ]),
  },
  {
    path: '/proximityGeofenceGroups',
    component: ProximityGeofenceGroupsList,
    meta: { title: 'names.proximityGeofenceGroup', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleSupplyOrgFeatureAccess(['adServer', 'inventoryManagement']),
      handleDemandOrgFeatureAccess(['dspCampaigns', 'dspDeals']),
      handleOrgSettingAccess('uiEnableGeofenceGroups'),
    ]),
  },
  {
    path: '/audienceGeofenceGroups',
    component: AudienceGeofenceGroupsList,
    meta: { title: 'names.audienceGeofenceGroup', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleOrgFeatureAccess('customAudiences'),
      handleOrgSettingAccess('uiEnableAudienceLogics'),
      handleOrgSettingAccess('uiEnableGeofenceGroups'),
    ]),
  },
  {
    path: '/audiences/:id',
    component: AudienceLogicDetails,
    meta: { title: 'names.audienceLogic' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleOrgFeatureAccess('customAudiences'),
      handleOrgSettingAccess('uiEnableAudienceLogics'),
    ]),
  },
  {
    path: '/audiences/:audienceLogicId/edit',
    component: EditAudienceLogic,
    meta: { title: 'actions.editAudienceLogic' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleOrgFeatureAccess('customAudiences'),
      handleOrgSettingAccess('uiEnableAudienceLogics'),
      handlePermissions(Permission.actions.WRITE, Permission.entities.AUDIENCELOGIC),
    ]),
    props: true,
  },
  {
    path: '/movie-audiences/:id/edit',
    component: EditMovieAudience,
    meta: { title: 'actions.editMovieAudience' },
    beforeEnter: multiguard([
      isNotChina,
      isOrganizationRoute,
      handleOrgFeatureAccess('customAudiences'),
      handleOrgSettingAccess('uiEnableMovieAudiences'),
      handlePermissions(Permission.actions.WRITE, Permission.entities.AUDIENCELOGIC),
    ]),
  },
  {
    path: '/airport-audiences/:id/edit',
    component: EditAirportAudience,
    meta: { title: 'actions.editAirportTravellerAudience' },
    beforeEnter: multiguard([
      isNotChina,
      isOrganizationRoute,
      handleOrgFeatureAccess('customAudiences'),
      handleOrgSettingAccess('uiEnableAirportAudiences'),
      handlePermissions(Permission.actions.WRITE, Permission.entities.AUDIENCELOGIC),
    ]),
  },
  {
    path: '/audiences',
    component: AudienceLogicsList,
    meta: { title: 'names.audienceLogic', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleOrgFeatureAccess('customAudiences'),
      handleOrgSettingAccess('uiEnableAudienceLogics'),
    ]),
  },
  {
    path: '/audience-audit',
    component: AudienceAuditDetails,
    meta: { title: 'names.audienceConcentration' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handleOrgFeatureAccess('customAudiences'),
      handleOrgSettingAccess('uiEnableAudienceAudit'),
    ]),
  },
  {
    path: '/inventory/operatinghours',
    component: EditOperatingHours,
    meta: { title: 'names.operatingHour', titlePlural: 2 },
    beforeEnter: isOrganizationRoute,
  },
  {
    path: '/sites',
    component: SitesList,
    meta: { title: 'names.site', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      isSupplyOrganizationRoute,
      handleSupplyOrgFeatureAccess('inventoryManagement'),
    ]),
  },
  {
    path: '/sites/:id',
    component: SiteDetails,
    meta: { title: 'names.site' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      isSupplyOrganizationRoute,
      handleSupplyOrgFeatureAccess('inventoryManagement'),
    ]),
  },
  {
    path: '/sites/:id/edit',
    component: EditSite,
    meta: { title: 'actions.editSite' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      isSupplyOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.SITE),
      handleSupplyOrgFeatureAccess('inventoryManagement'),
    ]),
  },
  {
    path: '/sites/:siteID/units/:id/edit',
    component: EditUnit,
    meta: { title: 'actions.editUnit' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      isSupplyOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.UNIT),
      handleSupplyOrgFeatureAccess('inventoryManagement'),
    ]),
  },
  {
    path: '/units',
    component: UnitsList,
    meta: { title: 'names.unit', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      isSupplyOrganizationRoute,
      handleSupplyOrgFeatureAccess('inventoryManagement'),
    ]),
  },
  {
    path: '/units/:id',
    component: UnitDetails,
    meta: { title: 'names.unit' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      isSupplyOrganizationRoute,
      handleSupplyOrgFeatureAccess('inventoryManagement'),
    ]),
  },
  {
    path: '/units/:id/edit',
    component: EditUnit,
    meta: { title: 'actions.editUnit' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      isSupplyOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.UNIT),
      handleSupplyOrgFeatureAccess('inventoryManagement'),
    ]),
  },
  {
    path: '/unit-packs',
    component: UnitPacksList,
    meta: { title: 'names.pack', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      isSupplyOrganizationRoute,
      handlePermissions(Permission.actions.READ, Permission.entities.UNITPACK),
      handleSupplyOrgFeatureAccess('inventoryManagement'),
    ]),
  },
  {
    path: '/unit-packs/:id',
    component: UnitPackDetails,
    meta: { title: 'names.pack' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      isSupplyOrganizationRoute,
      handlePermissions(Permission.actions.READ, Permission.entities.UNITPACK),
      handleSupplyOrgFeatureAccess('inventoryManagement'),
    ]),
  },
  {
    path: '/unit-packs/:id/edit',
    component: EditUnitPack,
    meta: { title: 'actions.editUnitPack' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      isSupplyOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.UNITPACK),
      handleSupplyOrgFeatureAccess('inventoryManagement'),
    ]),
  },
  {
    path: '/networks',
    component: NetworksList,
    meta: { title: 'names.network', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      isSupplyOrganizationRoute,
      handlePermissions(Permission.actions.READ, Permission.entities.NETWORK),
      handleSupplyOrgFeatureAccess('inventoryManagement'),
    ]),
  },
  {
    path: '/networks/:id',
    component: NetworkDetails,
    meta: { title: 'names.network' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      isSupplyOrganizationRoute,
      handlePermissions(Permission.actions.READ, Permission.entities.NETWORK),
      handleSupplyOrgFeatureAccess('inventoryManagement'),
    ]),
  },
  {
    path: '/networks/:id/edit',
    component: EditNetwork,
    meta: { title: 'actions.editNetwork' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      isSupplyOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.NETWORK),
      handleSupplyOrgFeatureAccess('inventoryManagement'),
    ]),
  },
  {
    path: '/avails-import',
    component: AvailsImportFilesList,
    meta: { title: 'names.availsImport' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      isSupplyOrganizationRoute,
      handleSupplyOrgFeatureAccess('inventoryManagement'),
      handleOrgSettingAccess('uiEnableAvailsImport'),
    ]),
  },
  {
    path: '/users',
    component: UsersList,
    meta: { title: 'names.user', titlePlural: 2 },
    beforeEnter: multiguard([isOrganizationRoute, handleOrgFeatureAccess('organizationManagement')]),
  },
  {
    path: '/org-custom-fields',
    component: OrganizationCustomFieldsList,
    meta: { title: 'names.organizationCustomField', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.READ, Permission.entities.ORGANIZATIONCUSTOMFIELD),
      handleOrgFeatureAccess(['adServer', 'dspCampaigns', 'dspDeals', 'ssp']),
    ]),
  },
  {
    path: '/org-custom-fields/:id',
    component: OrganizationCustomFieldDetails,
    meta: { title: 'names.organizationCustomField' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.READ, Permission.entities.ORGANIZATIONCUSTOMFIELD),
      handleOrgFeatureAccess(['adServer', 'dspCampaigns', 'dspDeals', 'ssp']),
    ]),
  },
  {
    path: '/org-custom-fields/:id/edit',
    component: EditOrganizationCustomField,
    meta: { title: 'actions.editOrganizationCustomField' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.ORGANIZATIONCUSTOMFIELD),
      handleOrgFeatureAccess(['adServer', 'dspCampaigns', 'dspDeals', 'ssp']),
    ]),
  },
  {
    path: '/postal-codes-files',
    component: PostalCodesFilesList,
    meta: { title: 'names.postalCodesFile', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      // handlePermissions(Permission.actions.READ, Permission.entities.AUDIENCELOGIC),
      // handleOrgFeatureAccess(['adServer', 'customAudiences']),
      // handleOrgSettingAccess('enablePostalCodesFiles'),
    ]),
  },
  {
    path: '/postal-codes-files/:id/edit',
    component: EditPostalCodesFile,
    meta: { title: 'actions.editPostalCodesFile' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      // handlePermissions(Permission.actions.WRITE, Permission.entities.AUDIENCELOGIC),
      // handleOrgFeatureAccess(['adServer', 'customAudiences']),
      // handleOrgSettingAccess('enablePostalCodesFiles'),
    ]),
  },
  {
    path: '/audience-files',
    component: AudienceFilesList,
    meta: { title: 'names.audienceFile', titlePlural: 2 },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.READ, Permission.entities.AUDIENCELOGIC),
      handleOrgFeatureAccess(['adServer', 'customAudiences']),
      handleOrgSettingAccess('uiEnableAudienceFiles'),
    ]),
  },
  {
    path: '/audience-files/:id',
    component: AudienceFileDetails,
    meta: { title: 'names.audienceFile' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.READ, Permission.entities.AUDIENCELOGIC),
      handleOrgFeatureAccess(['adServer', 'customAudiences']),
    ]),
  },
  {
    path: '/audience-files/:id/edit',
    component: EditAudienceFile,
    meta: { title: 'actions.editAudienceFile' },
    beforeEnter: multiguard([
      isOrganizationRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.AUDIENCELOGIC),
      handleOrgFeatureAccess(['adServer', 'customAudiences']),
    ]),
  },
  {
    path: '/org-settings',
    component: OrganizationSettingsDetails,
    meta: { title: 'names.organizationSetting', titlePlural: 2 },
    beforeEnter: isOrganizationRoute,
  },
  {
    path: '/organization-settings',
    component: OrganizationSettingsDetails,
    meta: { title: 'names.organizationSetting', titlePlural: 2 },
    beforeEnter: isOrganizationRoute,
  },
  {
    path: '/advanced-reports',
    component: AdvancedReportsList,
    meta: { title: 'names.advancedReport', titlePlural: 2 },
    beforeEnter: multiguard([isOrganizationRoute, handleOrgFeatureAccess('reporting')]),
  },
  {
    path: '/advanced-reports/:id/edit',
    component: AdvancedReportDefinition,
    meta: { title: 'actions.editAdvancedReport' },
    beforeEnter: multiguard([isOrganizationRoute, handleOrgFeatureAccess('reporting')]),
  },

  // Exchange UI
  {
    path: '/exch/deals',
    component: ExchangeDealList,
    meta: { title: 'names.deal', titlePlural: 2 },
    beforeEnter: isExchangeRoute,
  },
  {
    path: '/exch/deals/:id',
    component: ExchangeDealDetails,
    meta: { title: 'names.deal' },
    beforeEnter: isExchangeRoute,
  },
  {
    path: '/exch/deals/:id/edit',
    component: EditExchangeDeal,
    meta: { title: 'actions.editDeal' },
    beforeEnter: multiguard([
      isExchangeRoute,
      handlePermissions(Permission.actions.WRITE, Permission.entities.PACKAGE),
    ]),
  },
  {
    path: '/(.*)',
    redirect: '/',
  },

  //
  // Journey Analysis
  //
  {
    path: '/journeys/new',
    component: LocalCache.get('oldJourney') === true ? EditOldJourneyAnalysis : CrunchJourney,
    meta: { title: 'actions.newJourney' },
    beforeEnter: multiguard([
      isNotChina,
      isOrganizationRoute,
      handleOrgSettingAccess('enableJourneys'),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns', 'dspDeals']),
    ]),
  },
  {
    path: '/journeys/:id',
    component: LocalCache.get('oldJourney') === true ? OldJourneyAnalysisDetails : ViewJourney,
    meta: { title: 'names.journey' },
    beforeEnter: multiguard([
      isNotChina,
      isOrganizationRoute,
      handleOrgSettingAccess('enableJourneys'),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns', 'dspDeals']),
    ]),
  },
  {
    path: '/journeys/:id/edit',
    component: LocalCache.get('oldJourney') === true ? EditOldJourneyAnalysis : EditJourney,
    meta: { title: 'actions.editJourney' },
    beforeEnter: multiguard([
      isNotChina,
      isOrganizationRoute,
      handleOrgSettingAccess('enableJourneys'),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns', 'dspDeals']),
    ]),
  },
  {
    path: '/journeys',
    component: LocalCache.get('oldJourney') === true ? OldJourneyAnalysisList : JourneyList,
    meta: { title: 'names.journey', titlePlural: 2 },
    beforeEnter: multiguard([
      isNotChina,
      isOrganizationRoute,
      handleOrgSettingAccess('enableJourneys'),
      handleSupplyOrgFeatureAccess('adServer'),
      handleDemandOrgFeatureAccess(['dspCampaigns', 'dspDeals']),
    ]),
  },

  //
  // Header Bidder Presets
  //
  {
    path: '/header-bidder-presets/:id/edit',
    component: EditHeaderBidderPreset,
    meta: { title: 'actions.editHeaderBidderPreset' },
    beforeEnter: multiguard([isOrganizationRoute]),
  },
  {
    path: '/header-bidder-dashboard',
    component: HeaderBidderDashboard,
    meta: { title: 'names.dashboard' },
    beforeEnter: multiguard([isOrganizationRoute]),
  },
  {
    path: '/header-bidder-config',
    component: HeaderBidderSettingsGateway,
    meta: { title: 'messages.headerBidderSettings' },
    beforeEnter: multiguard([isOrganizationRoute, handleOrgSettingAccess('uiEnableMultiSSP')]),
  },
  {
    path: '/yield-manager-config',
    component: HeaderBidderSettingsGateway,
    meta: { title: 'messages.yieldOptimization' },
    beforeEnter: multiguard([isOrganizationRoute, handleOrgSettingAccess('uiEnableMultiSSP')]),
  },
  {
    path: '/yield-manager-dashboard',
    component: YieldManagerDashboard,
    meta: { title: 'messages.yieldManagerDashboard' },
    beforeEnter: multiguard([isOrganizationRoute, handleOrgSettingAccess('uiEnableMultiSSP')]),
  },
];

const router = createRouter({
  routes,
  history: createWebHistory(),
  linkActiveClass: 'active',
});

const publicRoutes = [
  '/heat/',
  '/lift/',
  '/proposal/',
  '/reset-password',
  '/password-reset',
  '/verify-email',
  '/oops',
  '/monitoring/',
];

/**
 * Check if `fullPath` is a public route
 * @param {string} fullPath
 * @returns {boolean} `true` if any of the public routes is included in `fullPath`
 */
export function isPublicRoute(fullPath) {
  return publicRoutes.some((publicRoute) => fullPath.includes(publicRoute));
}

router.beforeEach(async (to, from, next) => {
  rootStore.commit(rootTypes.UPDATE_IS_PUBLIC_ROUTE, isPublicRoute(to.fullPath));

  GlobalEventBus.$emit(GlobalEvents.clearSomethingWentWrongStatus);
  GlobalEventBus.$emit(GlobalEvents.clearTooltips);

  if (rootState.isAppOffline) {
    if (window.navigator.onLine === false) {
      rootStore.commit(rootTypes.UPDATE_IS_APP_OFFLINE, true);
    } else {
      return;
    }
  }
  if (window.navigator.onLine === false) {
    rootStore.commit(rootTypes.UPDATE_IS_APP_OFFLINE, false);
    return;
  }
  const isValidToken = () => {
    const token = LocalCache.get('token');
    if (!token) {
      return false;
    }

    const parseJwt = (jwt) => {
      const base64Url = jwt.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join(''),
      );

      return JSON.parse(jsonPayload);
    };

    const decoded = parseJwt(token);
    if (decoded == null) {
      return false;
    }
    const now = Date.now() / 1000.0;
    return now < decoded.exp;
  };
  const authenticated = LocalCache.get('authenticated') === 'true' && isValidToken();
  if (
    authenticated &&
    !rootState.isPublicRoute &&
    !(to.fullPath.includes('organization-selection') || to.fullPath.includes('login')) &&
    rootState.orgApp?.organization == null
  ) {
    // Aync/Await in beforeMount of APP.vue is not synchronous, so we need to wait for the organization to be set when page reloads.
    await waitUntil(() => rootState.orgApp.organization !== null, 1000, 5000);
  }
  const { organization } = rootState.orgApp;
  if (authenticated && to.path === loginPath.path) {
    next(membershipSelection);
  }

  if (to.path === '/' && authenticated && organization != null) {
    let route = '';
    if (organization.type === OrganizationType.CURATOR || organization.organizationFeatures.dashboard) {
      route = '/dashboard';
    } else if (organization.isDemandOrganization && organization.organizationFeatures.dspCampaigns) {
      route = '/campaigns';
    } else if (organization.isDemandOrganization && organization.organizationFeatures.dspDeals) {
      route = '/deals';
    } else if (organization.isSupplyOrganization && organization.organizationFeatures.adServer) {
      route = '/campaigns';
    } else if (organization.isSupplyOrganization && organization.organizationFeatures.ssp) {
      route = '/deals';
    } else if (organization.isSupplyOrganization && organization.organizationFeatures.approvals) {
      route = '/pendingapprovals';
    } else if (organization.isDemandOrganization && organization.organizationFeatures.approvals) {
      route = '/pending-submissions';
    } else if (organization.organizationFeatures.reporting) {
      route = '/reportdefinitions';
    } else if (organization.isSupplyOrganization && organization.organizationFeatures.inventoryManagement) {
      route = '/sites';
    } else if (region !== Regions.China && organization.organizationFeatures.customAudiences) {
      route = '/audiences';
    } else if (organization.organizationFeatures.organizationManagement) {
      route = '/users';
    }

    next({
      path: route,
      replace: true,
    });
  }
  const { exchange } = rootState.exchApp;
  if (to.path === '/' && authenticated && exchange != null) {
    next({
      path: '/exch/deals',
      replace: true,
    });
  }

  if (to.meta.title !== undefined) {
    if (
      to.meta.title.indexOf('names.') === 0 ||
      to.meta.title.indexOf('messages.') === 0 ||
      to.meta.title.indexOf('actions.') === 0
    ) {
      window.document.title = capitalize(i18n.global.t(to.meta.title, to.meta.titlePlural || 1));
    } else {
      window.document.title = capitalize(to.meta.title);
    }
  }

  // If the route is not public, and you're not authenticated, route to login
  if (!(to.meta.public !== undefined && to.meta.public === true)) {
    if (!isValidToken()) {
      await APIProxy.instance.attemptTokenRefresh(null);
    }

    if (!authenticated) {
      SessionCache.originalRoute = to.fullPath;
      next(loginPath);
    }
    // Whenever the url does not contain a query params of orgID and the route is not public
    // nor organization-selection, and we are logged in as an organization (not an exchange),
    // then add the orgID to the URL.
  }
  next();
});

router.afterEach((to) => {
  GoogleAnalyticsApi.pageView(to.path);
  //
  // Add organization ID in url.
  //
  if (
    rootState.authenticated &&
    rootState.chosenMembershipType === 'org' &&
    rootState.orgApp.organization != null &&
    !to.meta.public &&
    !to.fullPath.includes('organization-selection')
  ) {
    const orgId = rootState.orgApp.organization.id;
    if (!window.location.search.includes('orgID=')) {
      const keyValue = `orgID=${md5(orgId)}`;
      const url = window.location.search ? `${window.location.search}&${keyValue}` : `?${keyValue}`;
      window.history.replaceState(null, null, url);
    }
  }

  //
  // If a new version is available, this will force a refresh as the user is navigating through the app.
  //
  if (rootStore.state.isNewVersionAvailable) {
    window.location.reload();
    console.info('Application was automatically refreshed.');
  }
});

export default router;
